import './style.scss';

import {
  CSSProperties,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Carousel, CarouselProps, Image, Spin, Tooltip } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { createPortal } from 'react-dom';

export interface PhotoCarouselItem {
  photo: string;
  thumbnail?: string;
  description?: string;
}

export interface PhotoCarouselProps extends CarouselProps {
  items: PhotoCarouselItem[];
  style?: CSSProperties;
  className?: string;
  carouselStyle?: CSSProperties;
  carouselClassName?: string;
  previewLabel?: string;
  loading?: boolean;
}

export const PhotoCarousel = ({
  items,
  autoplay = false,
  className = '',
  style: wrapperStyle = {},
  carouselClassName = '',
  carouselStyle = {},
  previewLabel = 'Просмотр',
  loading = false,
  ...props
}: PhotoCarouselProps) => {
  const carouselRef = useRef<CarouselRef>(null);
  const [isFullscreen, setFullscreen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const toggleFullscreen = useCallback(() => {
    setFullscreen((prevState) => !prevState);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      /* Для работы клавиатурной навигации необходимо восстановить фокус
      на карусели. Так как у карусели нет специального метода
      для восстановления фокуса, устанавливаем фокус на активном слайде */
      const slick = carouselRef.current?.innerSlider;
      if (slick) {
        slick.list?.querySelector('.slick-active')?.focus();
      }
    }
  }, [isFullscreen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isFullscreen && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setFullscreen(false);
      }
    },
    [isFullscreen],
  );

  const wrapperClasses = `photo-carousel-wrapper ${className}`.trim();

  const slides = useMemo(() => {
    return items?.map((image: PhotoCarouselItem, index: number) => (
      <div className={'image-preview'} key={`pc-img-${index}`}>
        <Image
          src={image.thumbnail ?? image.photo}
          alt={image?.description ?? ''}
          preview={false}
          classNames={{
            root: 'photo-carousel-image-wrapper',
          }}
        />
      </div>
    ));
  }, [items]);

  return (
    <>
      <div className={wrapperClasses} style={wrapperStyle}>
        <Spin className={'spinner'} spinning={loading}>
          {items.length > 0 && (
            <>
              <Carousel
                autoplay={!isFullscreen && autoplay}
                arrows
                {...props}
                style={carouselStyle}
                className={`photo-carousel ${carouselClassName}`.trim()}
                afterChange={setCurrentSlide}
              >
                {slides}
              </Carousel>
              {!isFullscreen && (
                <div className="preview-cover" onClick={toggleFullscreen}>
                  <div className={'mask'}>
                    <EyeOutlined />
                    <span>{previewLabel}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </Spin>
      </div>
      {isFullscreen &&
        createPortal(
          <div
            className={'photo-carousel-wrapper fullscreen'}
            onKeyDown={handleKeyDown}
          >
            <>
              <Carousel
                arrows
                swipeToSlide
                ref={carouselRef}
                className={`photo-carousel`}
                initialSlide={currentSlide}
              >
                {slides}
              </Carousel>
              <Tooltip title="Закрыть">
                <CloseOutlined
                  onClick={toggleFullscreen}
                  className={'close-button'}
                />
              </Tooltip>
            </>
          </div>,
          document.body,
        )}
    </>
  );
};

export default PhotoCarousel;
